import { FC, useEffect, useState } from 'react'
import axios from 'axios'

import { Location } from '../../../../components'
import RFFFields from '../../react-final-form-fields'

import { toNumbers } from '../../../../util'
import { validateEmail, getValidateFileSizeLessThan, validateRequired } from '../../../../util/validation'
import config from '../../../../config'

import { renameFile } from '../../../../services'

import { SafetyReportFormFieldsProps } from './safety-report-form-types'
import { Accordion, Card } from 'react-bootstrap'

import s from './safety-report-accordion.module.scss'

const validateFile = (file: File) => getValidateFileSizeLessThan(20, 'MB')(file)

const uploadFile = async (file: File, portalKey: string) => {
  const url = `${config.baseUrl}/sms/SafetyReport/${portalKey}/Upload`

  const formData = new FormData()
  formData.append('portalKey', portalKey)
  formData.append('file', renameFile(file))

  return axios.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

const SafetyReportFormFields: FC<SafetyReportFormFieldsProps> = props => {
  const { portalKey, submitting, values, form } = props
  // Expand additional questions card if any question is required
  const hasRequiredQuestion = values.questions && values.questions.some(q => q.isRequired)
  const [cardOpen, setCardOpen] = useState<Record<string, boolean>>({
    0: !!hasRequiredQuestion,
    1: false,
  })

  useEffect(() => {
    if (hasRequiredQuestion) {
      setCardOpen(prev => ({ ...prev, 0: true }))
    }
  }, [hasRequiredQuestion])

  return (
    <>
      <RFFFields.DatePicker
        name="observedAt"
        label="When did you observe this?"
        validate={validateRequired}
        withAsterisk
        disabled={submitting}
        withPortal
      />

      <RFFFields.Textarea
        name="description"
        label="Description of Safety Concern"
        maxLength={2000}
        validate={validateRequired}
        withAsterisk
        disabled={submitting}
      />

      <div className="mb-7">
        <Location
          // @ts-ignore
          hideSavedLocations={true}
          mapMode={values?.mapMode}
          setMapMode={form?.mutators?.onMapModeChange}
          displayOptionalIndicator={true}
          entityLabel="concern"
          useExternalSMSDescription={true}
        />
      </div>

      <RFFFields.DocumentUpload
        name="documents"
        parentType="SafetyReport"
        uploadFile={async (file: File) => await uploadFile(file, portalKey)}
        validateSize={validateFile}
        acceptedFileExtensions=".jpg,.jpeg,.png,.heic,.heif,.dng,.raw"
        displayOptionalIndicator={true}
      />

      <Accordion>
        <Card className={`d-flex flex-column mr-4 mb-8 ${s['card']} ${s['accordion-heading']}`}>
          <Accordion.Toggle
            as={Card.Header}
            eventKey="0"
            className={s['heading']}
            onClick={() => {
              setCardOpen(prev => ({
                0: !cardOpen[0],
                1: !cardOpen[0] ? false : prev[1],
              }))
            }}
          >
            <h5>
              Additional Questions
              {hasRequiredQuestion && <span style={{ color: 'red' }}> *</span>}
            </h5>
            <div className={s['collapse-expand-icon']}>
              {cardOpen[0] ? <i className="bi bi-chevron-up ml-2" /> : <i className="bi bi-chevron-down ml-2" />}
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0" in={cardOpen[0]}>
            <Card.Body>
              {/* Render dynamic multiple choice questions */}
              {values.questions &&
                Array.isArray(values.questions) &&
                values.questions
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((question, idx) => (
                    <RFFFields.Select
                      key={question.id || idx}
                      name={`question_${question.id}`}
                      label={question.questionText}
                      options={question.answerOptions?.map(option => ({ label: option, value: option })) || []}
                      validate={question.isRequired ? validateRequired : undefined}
                      withAsterisk={question.isRequired}
                      disabled={submitting}
                    />
                  ))}
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        <Card className={`d-flex flex-column mr-4 mb-8 ${s['card']} ${s['accordion-heading']}`}>
          <Accordion.Toggle
            as={Card.Header}
            eventKey="1"
            className={s['heading']}
            onClick={() => {
              setCardOpen(prev => ({
                0: !cardOpen[1] ? false : prev[0],
                1: !cardOpen[1],
              }))
            }}
          >
            <h5>Reporter Information</h5>
            <div className={s['collapse-expand-icon']}>
              {cardOpen[1] ? <i className="bi bi-chevron-up ml-2" /> : <i className="bi bi-chevron-down ml-2" />}
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="1">
            <Card.Body>
              <RFFFields.Input name="reporterName" label="Name" placeholder={'Enter your name'} disabled={submitting} />

              <RFFFields.Input
                name="reporterEmailAddress"
                label="E-mail Address"
                placeholder={'john.doe@example.com'}
                validate={validateEmail}
                disabled={submitting}
              />

              <RFFFields.MaskedInput
                name="reporterPhoneNumber"
                label="Phone"
                type="phone"
                parse={toNumbers}
                disabled={submitting}
                placeholder={'___-___-____'}
              />
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </>
  )
}

export default SafetyReportFormFields
